<template>
  <div class="post" :class="{ 'post--deleted': isDeleted }">
    <div class="post__main d-block">
      <div class="d-flex">
        <div
          class="post__image"
          :class="{
            'post__image--empty': !hasImage
          }"
          :style="{
            backgroundImage: hasImage
              ? 'url(' + post.post_data.image_url[0].sourceUrl + ')'
              : ''
          }"
        >
          <template v-if="!hasImage">
            <v-icon color="primary" :size="iconSize" class="mb-1"
              >icon-photo-add</v-icon
            >
            <p>{{ $t("message.posts.list.noPhoto") }}</p>
          </template>
          <div class="badge badge--deleted" v-if="isDeleted">
            <v-chip color="pink">
              {{
                $t(
                  `message.posts.list.${
                    isProcessing ? "deletingInProgress" : "deleted"
                  }`
                )
              }}
            </v-chip>
          </div>
          <PostListItemCategory :category-name="categoryName" />
        </div>
        <div class="post__content">
          <p
            v-if="
              post &&
                post.post_data &&
                post.post_data.event &&
                post.post_data.event.title
            "
            class="post__title"
          >
            {{ post.post_data.event.title }}
          </p>
          <p v-if="post && post.post_data && post.post_data.text">
            {{ post.post_data.text }}
          </p>

          <div v-if="hasInsightsData" class="insight-data">
            <div class="insight-data__item">
              <v-icon color="primary" class="mr-2">
                icon-eye-outlined
              </v-icon>
              <p>
                {{
                  getViewsCountLabel(
                    gmbInsights[POSTS_REPORT_INSIGHTS.VIEWS_COUNT]
                  )
                }}
              </p>
            </div>
            <div class="insight-data__item">
              <v-icon color="primary" class="mr-2">
                icon-click
              </v-icon>
              <p>
                {{
                  getClicksLabel(
                    gmbInsights[POSTS_REPORT_INSIGHTS.CLICKS_COUNT] || 0,
                    post.post_data.call_to_action
                      ? post.post_data.call_to_action.actionType
                      : null
                  )
                }}
              </p>
            </div>
          </div>

          <div v-if="durationTime" class="post__duration-time">
            <p>{{ $t("message.posts.list.durationTime") }}</p>
            <div v-if="durationTime.from">
              <span>{{ $t("message.common.from") }}</span>
              <span>{{ durationTime.from }}</span>
            </div>
            <div v-if="durationTime.to">
              <span>{{ $t("message.common.to") }}</span>
              <span>{{ durationTime.to }}</span>
            </div>
          </div>
          <a
            v-if="post && post.post_data && post.post_data.search_url"
            class="post__show-details-btn"
            :href="post.post_data.search_url"
            target="_blank"
          >
            {{ $t("message.common.details") }}
          </a>
        </div>
      </div>
      <div v-if="hasInsightsData && !isDeleted" class="post__alert">
        <div
          v-html="$t('message.analytics.businessAddresses.postStatAlert2')"
        ></div>
      </div>
    </div>
    <div class="post__details-card details">
      <template v-if="!isDeleted">
        <div
          v-if="isProcessing"
          class="details__summary"
          @click="showFacilitiesDialog()"
        >
          <v-icon color="primary" class="mr-2" :size="iconSize">
            icon-status-pending
          </v-icon>
          <p>{{ $t("message.posts.list.sendingInProgress") }}</p>
          <button class="ml-1" @click="showFacilitiesDialog()">
            {{ getFacilitiesCountLabel(post.facilities_count) }}
          </button>
        </div>
        <div v-else-if="isFinished" class="details__summary">
          <v-icon color="primary" class="mr-2" :size="iconSize">
            icon-status-confirmed
          </v-icon>
          <p>
            {{ $t("message.posts.list.sentTo") }}
          </p>
          <button class="ml-1" @click="showFacilitiesDialog()">
            {{ getFacilitiesCountLabel(post.facilities_count) }}
          </button>
        </div>
      </template>
      <div class="details__wrapper">
        <div class="details__status">
          <p class="details__label">{{ $t("message.common.status") }}</p>
          <template v-if="!isDeleted">
            <p v-if="isProcessing" class="details__sent-stats">
              {{
                $t("message.posts.list.detailsStatusSent", {
                  sent_count: post.sent_posts_count,
                  total_count: post.facilities_count
                })
              }}
            </p>
            <p v-else-if="isFinished" class="details__sent-stats">
              {{ $t("message.posts.list.sendingFinished") }}
            </p>
          </template>
          <template v-else>
            <p v-if="isProcessing" class="details__sent-stats">
              {{ $t("message.posts.list.deletingInProgress") }}
            </p>
            <p v-else class="details__sent-stats">
              {{ $t("message.posts.list.deleted") }}
            </p>
          </template>
        </div>
        <div v-if="isFinished" class="details__details">
          <p class="details__label">{{ $t("message.common.details") }}</p>
          <div
            v-if="post.sent_posts_count > 0"
            class="details__sent-details sent-success"
          >
            <v-icon color="teal" class="mr-2" :size="iconSize">
              icon-status-success
            </v-icon>
            <button @click="showFacilitiesDialog(POST_BY_FACILITY_STATUS.OK)">
              {{
                $t("message.posts.list.sentSuccessCount", {
                  count: post.sent_posts_count
                })
              }}
            </button>
          </div>
          <div
            v-if="post.facilities_count - post.sent_posts_count > 0"
            class="details__sent-details sent-error"
          >
            <v-icon color="pink" class="mr-2" :size="iconSize">
              icon-status-warning
            </v-icon>
            <button
              @click="showFacilitiesDialog(POST_BY_FACILITY_STATUS.FAILED)"
            >
              {{
                $t("message.posts.list.sentErrorCount", {
                  count: post.facilities_count - post.sent_posts_count
                })
              }}
            </button>
          </div>
        </div>
      </div>
      <div v-if="hasStoreLocator">
        <PostListItemStoreLocatorVisibility :post="post" />
      </div>
    </div>
    <div class="post__dates">
      <div v-if="post.created_at" class="post__date">
        <p>{{ $t("message.posts.list.sentDate") }}</p>
        <p>{{ getDate(post.created_at) }}</p>
      </div>
      <div v-if="post.edited_at" class="post__date mt-2">
        <p>{{ $t("message.posts.list.editDate") }}</p>
        <p>{{ getDate(post.edited_at) }}</p>
      </div>
      <div v-if="post.deleted_at" class="post__date mt-2">
        <p>{{ $t("message.posts.list.deleteDate") }}</p>
        <p>{{ getDate(post.deleted_at) }}</p>
      </div>
    </div>
    <div class="post__actions">
      <!-- <v-btn icon class="mr-4">
        <v-icon size="24">icon-edit</v-icon>
      </v-btn> -->
      <v-btn
        icon
        v-if="post.can_be_deleted && !isDeleted"
        :disabled="isProcessing"
        @click="dialog.deletePost = true"
      >
        <v-icon color="pink" size="24">icon-trash</v-icon>
      </v-btn>
    </div>

    <PostFacilitiesDialog
      :is-open="dialog.facilitiesList"
      :post-id="post.id"
      :status="chosenSendingStatus"
      @close="closeFacilitiesDialog"
    />

    <v-dialog v-model="dialog.deletePost" max-width="450">
      <v-card class="pa-6">
        <p class="text-center">
          {{ $t("message.posts.list.confirmDeleteDialog.text") }}
        </p>

        <p class="dialog-description">
          {{ $t("message.posts.list.confirmDeleteDialog.description") }}
        </p>

        <div class="d-flex justify-center">
          <ui-button
            outlined
            class="mr-3"
            color="primary"
            @click="dialog.deletePost = false"
          >
            {{ $t("message.common.cancel") }}
          </ui-button>
          <ui-button color="primary" @click="deletePost">
            {{ $t("message.common.confirmDelete") }}
          </ui-button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { POSTS_CALL_TO_ACTION_TYPE } from "@/enums/posts.enum";
import { pluralCount } from "@/helpers/helpers";
import { isFeatureFlagEnabled } from "@/helpers/posthog";

import PostListItemCategory from "@/components/Posts/PostListItemCategory.vue";
import PostFacilitiesDialog from "@/components/Posts/dialog/PostFacilitiesDialog.vue";
import PostListItemStoreLocatorVisibility from "@/components/Posts/PostListItemStoreLocatorVisibility";
import { plannedPostItemMixin } from "@/helpers/mixins/planned-post-item";

export default {
  name: "PostListItem",
  components: {
    PostListItemCategory,
    PostFacilitiesDialog,
    PostListItemStoreLocatorVisibility
  },
  mixins: [plannedPostItemMixin],
  methods: {
    isFeatureFlagEnabled,
    deletePost() {
      const params = {
        id: this.post.id,
        numLocations: this.post.facilities_count,
        postType: this.postType,
        publishDate: this.getDate(this.post.created_at)
      };
      this.$emit("deletePost", params);
      this.dialog.deletePost = false;
    },
    showFacilitiesDialog(status) {
      this.chosenSendingStatus = status;
      this.dialog.facilitiesList = true;
    },
    closeFacilitiesDialog() {
      this.chosenSendingStatus = null;
      this.dialog.facilitiesList = false;
    },
    getDate(date) {
      return moment(date).format(FORMAT.DATE);
    },
    getFacilitiesCountLabel(count) {
      return `${pluralCount("message.pluralForms.facilityGenitive", count)}`;
    },
    getClicksLabel(count, actionType) {
      const countText = pluralCount("message.pluralForms.click", count);
      if (!actionType) {
        return countText;
      }

      const buttonText = this.$t("message.posts.list.clickInButton", {
        label: this.$t(
          `message.posts.callToAction.${POSTS_CALL_TO_ACTION_TYPE[actionType]}`
        )
      });

      return `${countText} ${buttonText}`;
    },
    getViewsCountLabel(count) {
      return `${pluralCount("message.pluralForms.view", count)}`;
    }
  }
};
</script>

<style lang="scss" scoped src="./PostListItem.scss"></style>
<style lang="scss" scoped>
.post {
  position: relative;
  min-height: 184px;
  width: 100%;
  padding: $spacing * 2.5;
  padding-left: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: $text;

  &__alert {
    margin-top: $spacing;
    border-radius: 8px;
    padding: $spacing * 0.8 $spacing * 1.5;
    @include font-size(12px);
    background: $gray-500;
    max-width: 300px;
    .link {
      color: black;
      font-weight: bold;
      text-decoration: underline;
      &:hover {
        color: $primary;
      }
    }

    ::v-deep .text-primary {
      color: $primary;
    }
  }

  &__main {
    display: flex;
    width: 42%;
    padding-right: $spacing * 3;
    font-weight: 400;
  }

  &__image {
    position: relative;
    width: 156px;
    min-width: 156px;
    height: 164px;
    border-radius: 20px;
    background-size: cover;
    background-position: center;
    margin-right: $spacing * 3;

    &--empty {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: $light-4;

      p {
        @include font-size(12px);
        margin-bottom: 0;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;

    p {
      @include font-size(12px);
      margin-bottom: 0;
    }
  }

  &__title {
    font-weight: 600;
  }

  &__duration-time {
    margin-top: $spacing * 1.5;

    p {
      color: $primary;
    }

    div {
      @include font-size(14px);
      font-weight: 500;

      span:first-child {
        opacity: 0.5;
        text-transform: lowercase;
        margin-right: $spacing;
      }
    }
  }

  &__show-details-btn {
    margin-top: $spacing * 1.5;
    @include font-size(14px);
    font-weight: 600;
    text-decoration: underline;
    color: $primary;
  }

  &__details-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: $light-4;
    border-radius: 20px;
    padding: $spacing * 2.5 $spacing * 3;
    width: 30%;
    height: 100%;
    @include font-size(12px);
  }

  &__dates {
    display: flex;
    flex-direction: column;
    width: 10%;
  }

  &__date {
    p {
      margin-bottom: 0;
      &:first-child {
        margin-bottom: 5px;
        @include font-size(12px);
        font-weight: 400;
      }
      &:last-child {
        @include font-size(14px);
        font-weight: 600;
        color: $primary;
      }
    }
  }

  &__actions {
    min-width: 36px;
  }

  &--deleted {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: $light;
      opacity: 0.5;
    }
  }
}

.insight-data {
  width: 100%;
  display: flex;
  align-items: center;
  margin-top: $spacing * 0.5;

  &__item {
    display: flex;
    align-items: center;

    &:first-child {
      margin-right: $spacing * 2;

      p {
        width: max-content;
      }
    }

    p {
      @include font-size(12px);
      color: $text;
    }
  }
}

.details {
  &__summary {
    display: flex;
    align-items: center;
    margin-bottom: $spacing * 2.5;

    p {
      margin-bottom: 0;
      line-height: 18px;
    }
  }

  &__wrapper {
    display: flex;
  }

  &__status,
  &__details {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding-right: $spacing * 1.5;
  }

  &__label {
    @include font-size(12px);
    font-weight: 400;
    color: $text;
    margin-bottom: $spacing;
  }

  &__sent-stats {
    @include font-size(14px);
  }

  &__sent-details {
    display: flex;
    &:not(:last-child) {
      margin-bottom: $spacing;
    }
  }

  &__summary,
  &__sent-details {
    button {
      font-weight: 600;
      text-decoration: underline;
      color: $primary;
    }

    &.sent-success button {
      @include font-size(14px);
      color: $teal;
    }

    &.sent-error button {
      @include font-size(14px);
      color: $pink;
    }
  }
}

.badge {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;

  &--deleted {
    & > .v-chip {
      @include font-size(12px);
      color: $light;
    }
  }
}

.dialog-description {
  @include font-size(12px);
  line-height: 17px;
  margin-bottom: $spacing * 3;
}
</style>
